import moment from "moment";

/** Fixed date ranges offered by the OEE Summary report. */
export enum OeeDatePresetEnum {
    LAST_7_DAYS = "last_7_days",
    LAST_30_DAYS = "last_30_days",
    LAST_90_DAYS = "last_90_days",
    MONTH_TO_DATE = "month_to_date",
}

export const OEE_DATE_PRESETS: OeeDatePresetEnum[] = [
    OeeDatePresetEnum.LAST_7_DAYS,
    OeeDatePresetEnum.LAST_30_DAYS,
    OeeDatePresetEnum.LAST_90_DAYS,
    OeeDatePresetEnum.MONTH_TO_DATE,
];

/**
 * Month to date, so the OEE report opens on the same period as every other mill report:
 * MillDashboardsContext seeds its range with getFirstDayOfTheMonthAtZeroHour().
 */
export const DEFAULT_OEE_DATE_PRESET = OeeDatePresetEnum.MONTH_TO_DATE;

export interface IOeeDateRange {
    endDate: Date;
    startDate: Date;
}

export interface IProductionDateRange {
    endDate: string;
    startDate: string;
}

const TRAILING_DAYS: Record<Exclude<OeeDatePresetEnum, OeeDatePresetEnum.MONTH_TO_DATE>, number> = {
    [OeeDatePresetEnum.LAST_7_DAYS]: 7,
    [OeeDatePresetEnum.LAST_30_DAYS]: 30,
    [OeeDatePresetEnum.LAST_90_DAYS]: 90,
};

/** Shape of the production_date the OEE SQL functions take and return; the single declaration for the report. */
export const PRODUCTION_DATE_FORMAT = "YYYY-MM-DD";

/**
 * Resolves a preset to local start/end-of-day dates, today included. The dates
 * go through getStartOfDateRange/getEndOfDateRange like every other report, so
 * the query covers exactly the picked calendar days.
 */
export const resolveOeeDatePreset = (preset: OeeDatePresetEnum, today: Date = new Date()): IOeeDateRange => {
    const startDate =
        preset === OeeDatePresetEnum.MONTH_TO_DATE
            ? moment(today).startOf("month")
            : moment(today)
                  .startOf("day")
                  .subtract(TRAILING_DAYS[preset] - 1, "days");

    return { endDate: moment(today).endOf("day").toDate(), startDate: startDate.toDate() };
};

export const toProductionDateRange = (range: IOeeDateRange): IProductionDateRange => ({
    endDate: moment(range.endDate).format(PRODUCTION_DATE_FORMAT),
    startDate: moment(range.startDate).format(PRODUCTION_DATE_FORMAT),
});

export const formatOeeDateRangeLabel = (range: IOeeDateRange, dateFormat: string): string =>
    `${moment(range.startDate).format(dateFormat)} – ${moment(range.endDate).format(dateFormat)}`;
